import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  FaUsers, FaBriefcase, FaUser, FaBuilding, FaSearch, FaEye, 
  FaTimes, FaTrash, FaStar, FaMapMarkerAlt, FaClock,
  FaPause, FaPlay, FaChartBar, FaCheck, FaHourglassHalf,
  FaExclamationTriangle
} from 'react-icons/fa';

export default function AdminTalents() {
  const { API, user } = useContext(AppContext);
  
  const [ads, setAds] = useState([]);
  const [pendingAds, setPendingAds] = useState([]);
  const [stats, setStats] = useState({ total_ads: 0, job_offers: 0, profiles: 0, pending_count: 0, top_specialties: [] });
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  
  // Rejection modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (API && user) {
      fetchData();
    }
  }, [API, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data including pending
      const [pendingRes, activeRes, pausedRes, closedRes, rejectedRes, statsRes, specRes] = await Promise.all([
        axios.get(`${API}/talents?status=pending&limit=100`).catch(e => ({ data: { ads: [] } })),
        axios.get(`${API}/talents?status=active&limit=100`).catch(e => ({ data: { ads: [] } })),
        axios.get(`${API}/talents?status=paused&limit=100`).catch(e => ({ data: { ads: [] } })),
        axios.get(`${API}/talents?status=closed&limit=100`).catch(e => ({ data: { ads: [] } })),
        axios.get(`${API}/talents?status=rejected&limit=100`).catch(e => ({ data: { ads: [] } })),
        axios.get(`${API}/talents/stats`).catch(e => ({ data: { total_ads: 0, job_offers: 0, profiles: 0, pending_count: 0, top_specialties: [] } })),
        axios.get(`${API}/talents/specialties`).catch(e => ({ data: [] }))
      ]);
      
      setPendingAds(pendingRes.data.ads || []);
      
      const allAds = [
        ...(activeRes.data.ads || []),
        ...(pausedRes.data.ads || []),
        ...(closedRes.data.ads || []),
        ...(rejectedRes.data.ads || [])
      ];
      
      setAds(allAds);
      setStats(statsRes.data || { total_ads: 0, job_offers: 0, profiles: 0, pending_count: 0, top_specialties: [] });
      setSpecialties(specRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (ad) => {
    try {
      await axios.put(`${API}/talents/${ad.id}/status?status=active&user_id=${user.id}`);
      toast.success('Annonce validée et mise en ligne !');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleReject = async () => {
    if (!selectedAd) return;
    
    try {
      await axios.put(`${API}/talents/${selectedAd.id}/status?status=rejected&user_id=${user.id}&rejection_reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('Annonce refusée');
      setShowRejectModal(false);
      setSelectedAd(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du refus');
    }
  };

  const handleStatusChange = async (adId, newStatus, userId) => {
    try {
      await axios.put(`${API}/talents/${adId}/status?status=${newStatus}&user_id=${user.id}`);
      toast.success(`Statut mis à jour: ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (adId, userId) => {
    if (!window.confirm('Supprimer cette annonce définitivement ?')) return;
    
    try {
      await axios.delete(`${API}/talents/${adId}?user_id=${user.id}`);
      toast.success('Annonce supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getFilteredAds = () => {
    return ads.filter(ad => {
      if (statusFilter !== 'all' && ad.status !== statusFilter) return false;
      if (typeFilter !== 'all' && ad.ad_type !== typeFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          ad.title?.toLowerCase().includes(query) ||
          ad.user_name?.toLowerCase().includes(query) ||
          ad.company_name?.toLowerCase().includes(query) ||
          ad.location?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  };

  const getSpecialtyName = (id) => {
    const spec = specialties.find(s => s.id === id);
    return spec ? spec.name_fr : id;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-orange-500">En attente</Badge>;
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-500">En pause</Badge>;
      case 'closed':
        return <Badge className="bg-gray-500">Clôturée</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">Refusée</Badge>;
      case 'expired':
        return <Badge className="bg-red-500">Expirée</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredAds = getFilteredAds();
  const activeCount = ads.filter(a => a.status === 'active').length;
  const pausedCount = ads.filter(a => a.status === 'paused').length;
  const rejectedCount = ads.filter(a => a.status === 'rejected').length;

  const renderAdCard = (ad, showValidationButtons = false) => (
    <Card key={ad.id} className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            ad.ad_type === 'job_offer' ? 'bg-[#635BFF]/10' : 'bg-[#00D4FF]/10'
          }`}>
            {ad.ad_type === 'job_offer' ? (
              <FaBuilding className="text-xl text-[#635BFF]" />
            ) : (
              <FaUser className="text-xl text-[#00D4FF]" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {getStatusBadge(ad.status)}
              <Badge variant="outline">
                {ad.ad_type === 'job_offer' ? 'Offre' : 'Profil'}
              </Badge>
              {ad.is_featured && (
                <Badge className="bg-yellow-400 text-yellow-900">
                  <FaStar className="mr-1" /> Premium
                </Badge>
              )}
            </div>

            <h3 className="text-lg font-semibold text-[#0A2540]">
              {ad.title}
            </h3>

            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
              <span className="font-medium text-gray-700">
                {ad.user_name || ad.company_name || 'Anonyme'}
              </span>
              {ad.user_email && (
                <span className="text-gray-400">{ad.user_email}</span>
              )}
              {ad.location && (
                <span className="flex items-center gap-1">
                  <FaMapMarkerAlt /> {ad.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <FaClock /> {formatDate(ad.created_at)}
              </span>
            </div>

            {/* Description preview */}
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">
              {ad.description}
            </p>

            {/* Specialties */}
            <div className="flex flex-wrap gap-1 mt-2">
              {ad.specialties?.slice(0, 4).map(spec => (
                <span key={spec} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                  {getSpecialtyName(spec)}
                </span>
              ))}
              {ad.specialties?.length > 4 && (
                <span className="text-xs text-gray-400">+{ad.specialties.length - 4}</span>
              )}
            </div>

            {/* Rejection reason if rejected */}
            {ad.status === 'rejected' && ad.rejection_reason && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <strong>Raison du refus:</strong> {ad.rejection_reason}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 lg:flex-shrink-0">
            {showValidationButtons ? (
              <>
                <Button 
                  onClick={() => handleApprove(ad)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <FaCheck className="mr-1" /> Valider
                </Button>
                <Button 
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => {
                    setSelectedAd(ad);
                    setShowRejectModal(true);
                  }}
                >
                  <FaTimes className="mr-1" /> Refuser
                </Button>
                <Link to={`/talents/${ad.id}`} target="_blank">
                  <Button variant="outline" size="sm" className="w-full">
                    <FaEye className="mr-1" /> Voir
                  </Button>
                </Link>
              </>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Link to={`/talents/${ad.id}`} target="_blank">
                  <Button variant="outline" size="sm">
                    <FaEye className="mr-1" /> Voir
                  </Button>
                </Link>
                
                {ad.status === 'active' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleStatusChange(ad.id, 'paused', ad.user_id)}
                  >
                    <FaPause className="mr-1" /> Pause
                  </Button>
                )}
                
                {ad.status === 'paused' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-green-600 border-green-600"
                    onClick={() => handleStatusChange(ad.id, 'active', ad.user_id)}
                  >
                    <FaPlay className="mr-1" /> Activer
                  </Button>
                )}

                {ad.status === 'rejected' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-green-600 border-green-600"
                    onClick={() => handleStatusChange(ad.id, 'active', ad.user_id)}
                  >
                    <FaCheck className="mr-1" /> Valider
                  </Button>
                )}

                {ad.status !== 'closed' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleStatusChange(ad.id, 'closed', ad.user_id)}
                  >
                    <FaTimes className="mr-1" /> Clôturer
                  </Button>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleDelete(ad.id, ad.user_id)}
                >
                  <FaTrash />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540]">Gestion Talents & Recrutement</h1>
            <p className="text-gray-600">Valider et gérer les annonces d'emploi et profils</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className={`cursor-pointer ${activeTab === 'pending' ? 'ring-2 ring-orange-500' : ''}`} onClick={() => setActiveTab('pending')}>
            <CardContent className="p-4 text-center">
              <FaHourglassHalf className="text-2xl text-orange-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-orange-500">{pendingAds.length}</p>
              <p className="text-xs text-gray-500">En attente</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <FaUsers className="text-2xl text-[#635BFF] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0A2540]">{stats?.total_ads || 0}</p>
              <p className="text-xs text-gray-500">Total actives</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <FaBuilding className="text-2xl text-[#635BFF] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#635BFF]">{stats?.job_offers || 0}</p>
              <p className="text-xs text-gray-500">Offres</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <FaUser className="text-2xl text-[#00D4FF] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#00D4FF]">{stats?.profiles || 0}</p>
              <p className="text-xs text-gray-500">Profils</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => setActiveTab('all')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{activeCount}</p>
              <p className="text-xs text-gray-500">Actives</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
              <p className="text-xs text-gray-500">Refusées</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <FaHourglassHalf className="text-orange-500" />
              En attente ({pendingAds.length})
            </TabsTrigger>
            <TabsTrigger value="all">
              Toutes les annonces ({ads.length})
            </TabsTrigger>
          </TabsList>

          {/* Pending Tab */}
          <TabsContent value="pending">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-500">Chargement...</p>
              </div>
            ) : pendingAds.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FaCheck className="text-6xl text-green-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#0A2540] mb-2">Aucune annonce en attente</h3>
                  <p className="text-gray-500">Toutes les annonces ont été traitées</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-orange-700">
                    <FaExclamationTriangle />
                    <span className="font-medium">{pendingAds.length} annonce(s) en attente de validation</span>
                  </div>
                </div>
                {pendingAds.map(ad => renderAdCard(ad, true))}
              </div>
            )}
          </TabsContent>

          {/* All Ads Tab */}
          <TabsContent value="all">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher par titre, auteur, entreprise..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="active">Actives</SelectItem>
                      <SelectItem value="paused">En pause</SelectItem>
                      <SelectItem value="closed">Clôturées</SelectItem>
                      <SelectItem value="rejected">Refusées</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="job_offer">Offres d'emploi</SelectItem>
                      <SelectItem value="profile">Profils</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <p className="text-gray-600 mb-4">{filteredAds.length} annonce(s) trouvée(s)</p>

            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-[#635BFF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-500">Chargement...</p>
              </div>
            ) : filteredAds.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FaBriefcase className="text-6xl text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#0A2540] mb-2">Aucune annonce</h3>
                  <p className="text-gray-500">Aucune annonce ne correspond à vos critères</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredAds.map(ad => renderAdCard(ad, false))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Top Specialties */}
        {stats?.top_specialties?.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FaChartBar className="text-[#635BFF]" /> Top Spécialités
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {stats.top_specialties.map(spec => (
                  <div key={spec._id} className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-[#635BFF]">{spec.count}</p>
                    <p className="text-sm text-gray-600">{getSpecialtyName(spec._id)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Rejection Modal */}
      {showRejectModal && selectedAd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">Refuser l'annonce</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Vous allez refuser l'annonce: <strong>{selectedAd.title}</strong>
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison du refus (optionnel)
                </label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Expliquez pourquoi cette annonce est refusée..."
                  rows={4}
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedAd(null);
                    setRejectionReason('');
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleReject}
                >
                  Confirmer le refus
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}
